import { resolveInviteEmailAppUrl } from './memberInviteLogic';

export interface SendConnectionInviteParams {
  to: string;
  trainerName: string;
  appUrl?: string;
  /** Caller must pass authenticated trainer id (Edge verifies relationship ownership). */
  trainerId: string;
  /** When set, recipient email is validated server-side against this relationship. */
  relationshipId?: string | null;
}

/**
 * Sends a connection-invitation email via the Supabase Edge Function.
 * Uses direct fetch with anon key to avoid 403 from JWT verification when
 * the function is deployed with verify_jwt = false.
 */
export async function sendConnectionInvite(params: SendConnectionInviteParams): Promise<void> {
  const { to, trainerName, appUrl: appUrlParam, trainerId, relationshipId } = params;
  const appUrl = resolveInviteEmailAppUrl(appUrlParam);
  const body = {
    to: to.trim(),
    trainerName: (trainerName || 'Your trainer').trim(),
    appUrl,
    trainerId: trainerId.trim(),
    ...(relationshipId ? { relationshipId } : {}),
  };

  const url = process.env.REACT_APP_SUPABASE_URL;
  const anonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
  if (!url || !anonKey) {
    throw new Error('Missing REACT_APP_SUPABASE_URL or REACT_APP_SUPABASE_ANON_KEY');
  }

  const functionUrl = `${url.replace(/\/$/, '')}/functions/v1/send-connection-invite`;
  const res = await fetch(functionUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${anonKey}`,
    },
    body: JSON.stringify(body),
  });

  const data = await res.json().catch(() => null);

  if (process.env.NODE_ENV !== 'production') {
    console.log('[Invite email] Edge Function response:', {
      status: res.status,
      data,
      body: { ...body, to: body.to ? '[redacted]' : undefined },
    });
  }

  if (!res.ok) {
    const msg =
      data && typeof data === 'object' && 'error' in data && data.error
        ? String(data.error)
        : `Edge Function returned ${res.status}`;
    throw new Error(msg);
  }

  if (data && typeof data === 'object' && 'error' in data && data.error) {
    throw new Error(String(data.error));
  }
}

export interface SendInviteAcceptedParams {
  trainerId: string;
  relationshipId: string;
  appUrl?: string;
}

/**
 * Emails the trainer that a member accepted their invite (Edge Function).
 * Caller should gate with shouldAttemptInviteEmail() and fail soft.
 * CTA links use the same dynamic appUrl as connection-invite emails.
 */
export async function sendInviteAcceptedEmail(params: SendInviteAcceptedParams): Promise<void> {
  const trainerId = params.trainerId.trim();
  const relationshipId = params.relationshipId.trim();
  const appUrl = resolveInviteEmailAppUrl(params.appUrl);

  const body = {
    trainerId,
    relationshipId,
    ...(appUrl ? { appUrl } : {}),
  };

  const url = process.env.REACT_APP_SUPABASE_URL;
  const anonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
  if (!url || !anonKey) {
    throw new Error('Missing REACT_APP_SUPABASE_URL or REACT_APP_SUPABASE_ANON_KEY');
  }

  const functionUrl = `${url.replace(/\/$/, '')}/functions/v1/send-invite-accepted`;
  const res = await fetch(functionUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${anonKey}`,
    },
    body: JSON.stringify(body),
  });

  const data = await res.json().catch(() => null);

  if (process.env.NODE_ENV !== 'production') {
    console.log('[Invite accepted email] Edge Function response:', {
      status: res.status,
      data,
      body: { trainerId: '[redacted]', relationshipId: '[redacted]', appUrl: body.appUrl },
    });
  }

  if (!res.ok) {
    const msg =
      data && typeof data === 'object' && 'error' in data && data.error
        ? String(data.error)
        : `Edge Function returned ${res.status}`;
    throw new Error(msg);
  }

  if (data && typeof data === 'object' && 'error' in data && data.error) {
    throw new Error(String(data.error));
  }
}
